"use client"

import {useState, useEffect, useRef, useContext} from "react"
import { useNavigate } from "react-router-dom"
import {
    Box,
    Typography,
    Button,
    Container,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Snackbar,
    Alert,
} from "@mui/material"
import { Check } from "lucide-react"
import background from "../assets/image3.png"
import { useShop } from "../Context/ShopContext"
import {BackgroundContext} from "../App";
import text from "../text.json";

// Using the same categories from the tag selector


const CategorySelector = () => {
    const [selectedCategories, setSelectedCategories] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(false)
    const navigate = useNavigate()
    const maxCategories = 5

    const { currentBackground, language } = useContext(BackgroundContext)
    const langKey = language === "English" ? "en" : "es"

    const catViewText = text[langKey].categorySelector
    const cat2ViewText = text[langKey].categories


    // Get shop data refresh function
    const { refreshUserData, userId } = useShop()
    const hasRefreshedRef = useRef(false)


    const CATEGORIES = [
        cat2ViewText["Math"],
        cat2ViewText["Science"],
        cat2ViewText["Literature"],
        cat2ViewText["History"],
        cat2ViewText["Geography"],
        cat2ViewText["Foreign Languages"],
        cat2ViewText["Art"],
        cat2ViewText["Music"],
        cat2ViewText["Physical Education"],
        cat2ViewText["Technology"],
        cat2ViewText["Business Studies"],
        cat2ViewText["Philosophy"],
        cat2ViewText["Psychology"],
        cat2ViewText["Sociology"],
        cat2ViewText["Economics"],
        cat2ViewText["Health Education"],
        cat2ViewText["Home Economics"],
        cat2ViewText["Public Speaking"],
        cat2ViewText["Technology & Engineering"],
        cat2ViewText["Debate"],
        cat2ViewText["Environmental Science"],
        cat2ViewText["Theatre"],
        cat2ViewText["Law"],
        cat2ViewText["Education"],
        cat2ViewText["Career Development"],
    ]

    // Add effect to refresh skin data
    useEffect(() => {
        // This ensures the skin data is refreshed when the category selection component mounts
        const refreshSkinData = async () => {
            if (!hasRefreshedRef.current && userId) {
                console.log("[CategorySelection] Refreshing skin data on initial load")
                hasRefreshedRef.current = true

                try {
                    const success = await refreshUserData()
                    if (success) {
                        console.log("[CategorySelection] Successfully refreshed skin data")
                    } else {
                        console.warn("[CategorySelection] Failed to refresh skin data")
                    }
                } catch (err) {
                    console.error("[CategorySelection] Error refreshing skin data:", err)
                }
            }
        }

        refreshSkinData()
    }, [refreshUserData, userId])

    useEffect(() => {
        // Check if user is logged in
        const token = sessionStorage.getItem("token")
        const userId = sessionStorage.getItem("user")

        if (!token || !userId) {
            console.log("[CategorySelection] Refreshing skin data on initial load")
            // navigate("/login")
            return
        }

        // Check if user already has category badges
        checkExistingBadges()
    }, [navigate])

    const checkExistingBadges = async () => {
        setIsLoading(true)
        const token = sessionStorage.getItem("token")
        const userId = sessionStorage.getItem("user")

        try {
            // Fetch user badges
            const response = await fetch(`${process.env.REACT_APP_API_PATH}/user-badge?userID=${userId}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            })

            if (!response.ok) {
                throw new Error("Failed to fetch user badges")
            }

            const data = await response.json()

            // Check if user has any category badges
            if (data && data[0] && data[0].length > 0) {
                // Extract category names from badge attributes
                const categoryBadges = data[0].filter((badge) => badge.attributes && badge.attributes.type === "category")

                if (categoryBadges.length > 0) {
                    // User already has category badges, redirect to home
                    navigate("/")
                    return
                }
            }

            setIsLoading(false)
        } catch (error) {
            console.error("Error checking badges:", error)
            setIsLoading(false)
        }
    }

    const handleCategorySelection = (category) => {
        if (selectedCategories.includes(category)) {
            // Remove category
            setSelectedCategories(selectedCategories.filter((c) => c !== category))
        } else if (selectedCategories.length < maxCategories) {
            // Add category
            setSelectedCategories([...selectedCategories, category])
        }
    }

    const handleSaveCategories = async () => {
        if (selectedCategories.length === 0) {
            setError("Please select at least one category")
            return
        }

        setIsSaving(true)
        setError(null)

        const token = sessionStorage.getItem("token")
        const userId = sessionStorage.getItem("user")

        try {
            // Create badges for each selected category
            const badgePromises = selectedCategories.map(async (category) => {
                // First check if badge exists for this category
                const badgeResponse = await fetch(
                    `${process.env.REACT_APP_API_PATH}/badge?name=${encodeURIComponent(category)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    },
                )

                const badgeData = await badgeResponse.json()
                let badgeId

                // If badge doesn't exist, create it
                if (!badgeData || !badgeData[0] || badgeData[0].length === 0) {
                    const createBadgeResponse = await fetch(`${process.env.REACT_APP_API_PATH}/badge`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            name: category,
                            description: `Interest in ${category}`,
                            attributes: {
                                type: "category",
                                visible: false,
                            },
                        }),
                    })

                    if (!createBadgeResponse.ok) {
                        throw new Error(`Failed to create badge for ${category}`)
                    }

                    const newBadge = await createBadgeResponse.json()
                    badgeId = newBadge.id
                } else {
                    // Use existing badge
                    badgeId = badgeData[0][0].id
                }

                // Assign badge to user
                const assignBadgeResponse = await fetch(`${process.env.REACT_APP_API_PATH}/user-badge`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userID: userId,
                        badgeID: badgeId,
                        attributes: {
                            type: "category",
                            category: category,
                        },
                    }),
                })

                if (!assignBadgeResponse.ok) {
                    throw new Error(`Failed to assign ${category} badge to user`)
                }

                return assignBadgeResponse.json()
            })

            // Wait for all badges to be created and assigned
            await Promise.all(badgePromises)

            setSuccess(true)

            // Refresh skin data one more time before redirecting to ensure it's properly loaded
            try {
                console.log("[CategorySelection] Final skin data refresh before redirect")
                await refreshUserData()
            } catch (err) {
                console.warn("[CategorySelection] Error in final skin refresh:", err)
                // Continue with redirect even if refresh fails
            }

            // Redirect to home page after a short delay and force page refresh
            setTimeout(() => {
                console.log("[CategorySelection] Redirecting to home page after saving categories")
                // First navigate to home
                navigate("/")
                // Then force a page refresh
                window.location.reload(true)
            }, 1500)
        } catch (error) {
            console.error("Error saving categories:", error)
            setError("Failed to save your categories. Please try again.")
        } finally {
            setIsSaving(false)
        }
    }

    const handleSkip = async () => {
        // Refresh skin data before navigating to home
        try {
            console.log("[CategorySelection] Refreshing skin data before skipping to homepage")
            await refreshUserData()
        } catch (err) {
            console.warn("[CategorySelection] Error refreshing skin data during skip:", err)
            // Continue with redirect even if refresh fails
        }

        // Redirect to home page and force page refresh
        navigate("/")
        window.location.reload(true)
    }

    if (isLoading) {
        return (
            <Box
                sx={{
                    flexGrow: 1,
                    bgcolor: "#1D1D20",
                    minHeight: "100vh",
                    backgroundImage: `url(${background})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    opacity: 1.0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress sx={{ color: "#FFFFFF" }} />
            </Box>
        )
    }

    return (
        <Box
            sx={{
                flexGrow: 1,
                bgcolor: "#1D1D20",
                minHeight: "100vh",
                backgroundImage: `url(${background})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                opacity: 1.0,
            }}
        >
            <Container
                maxWidth="md"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    py: 4,
                }}
            >
                <Box
                    sx={{
                        bgcolor: "#FFFFFF",
                        py: 8,
                        px: 4,
                        width: "100%",
                        borderRadius: 2,
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        color="#1D1D20"
                        gutterBottom
                        sx={{
                            fontFamily: "SourGummy, sans-serif",
                            fontWeight: 800,
                            fontSize: "42px",
                        }}
                    >
                        {catViewText.title}
                    </Typography>
                    <Typography
                        variant="h5"
                        align="center"
                        color="#1D1D20"
                        paragraph
                        sx={{
                            fontFamily: "SourGummy, sans-serif",
                            fontWeight: 600,
                            fontSize: "22px",
                            mb: 4,
                        }}
                    >
                        {catViewText.subtitle1}   {maxCategories}  {catViewText.subtitle2}
                    </Typography>

                    <Typography
                        variant="body1"
                        align="center"
                        color="#1D1D20"
                        paragraph
                        sx={{
                            fontFamily: "SourGummy, sans-serif",
                            fontWeight: 500,
                            fontSize: "16px",
                            mb: 4,
                        }}
                    >
                        {catViewText.description}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        {CATEGORIES.map((category) => (
                            <Grid item xs={6} sm={4} md={3} key={category}>
                                <Card
                                    onClick={() => handleCategorySelection(category)}
                                    sx={{
                                        cursor: "pointer",
                                        bgcolor: selectedCategories.includes(category) ? "#1D6EF1" : "#F4FDFF",
                                        color: selectedCategories.includes(category) ? "white" : "#1D1D20",
                                        border: selectedCategories.includes(category) ? "2px solid #1D6EF1" : "2px solid #E9D0CE",
                                        borderRadius: 2,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                        position: "relative",
                                        height: "100%",
                                        opacity:
                                            selectedCategories.length >= maxCategories && !selectedCategories.includes(category) ? 0.5 : 1,
                                    }}
                                >
                                    <CardContent sx={{ p: 2, textAlign: "center" }}>
                                        <Typography
                                            sx={{
                                                fontFamily: "SourGummy, sans-serif",
                                                fontWeight: 600,
                                                fontSize: "14px",
                                            }}
                                        >
                                            {category}
                                        </Typography>
                                        {selectedCategories.includes(category) && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    bgcolor: "white",
                                                    borderRadius: "50%",
                                                    width: 20,
                                                    height: 20,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Check size={14} color="#1D6EF1" />
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                        <Button
                            onClick={handleSkip}
                            sx={{
                                fontFamily: "SourGummy, sans-serif",
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "#1D1D20",
                                "&:hover": {
                                    bgcolor: "rgba(29, 29, 32, 0.05)",
                                },
                            }}
                        >
                            {catViewText.skipButton}
                        </Button>
                        <Button
                            onClick={handleSaveCategories}
                            disabled={selectedCategories.length === 0 || isSaving}
                            sx={{
                                bgcolor: "#EF7B6C",
                                "&:hover": {
                                    bgcolor: "#e66a59",
                                },
                                fontFamily: "SourGummy, sans-serif",
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "#F4FDFF",
                                px: 4,
                                py: 1,
                            }}
                        >
                            {isSaving ? catViewText.saving : catViewText.saveButton}
                        </Button>
                    </Box>

                    <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography
                            variant="body2"
                            color="#1D1D20"
                            sx={{
                                fontFamily: "SourGummy, sans-serif",
                                fontWeight: 500,
                                fontSize: "14px",
                            }}
                        >
                            {catViewText.selectedCount} {selectedCategories.length}/{maxCategories}
                        </Typography>
                    </Box>
                </Box>
            </Container>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={success}
                autoHideDuration={6000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: "100%" }}>
                    Preferences saved successfully!
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default CategorySelector
